import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import RichTextEditor from "../../RichTextEditor/RichTextEditor";

const RowActions = { Edit: 0, Add: 1, Delete: 2 };
const RichTextCellEditor = ({ initialValue, onChange }) => {
const [localValue, setLocalValue] = useState(initialValue || "");

	return (
		<Box border="1px solid #ccc" borderRadius={1} p={1} sx={{ background: "#fff" }}>
			<RichTextEditor
				value={localValue}
				placeholder="Enter description..."
				onChange={(value) => {
					setLocalValue(value);
					onChange(value);
				}}
			/>
		</Box>
	);
};

const Appendix = ({ appendix, refetchMonitoringData, vesselMstId }) => {
	const [data, setData] = useState([]);
	const [activeAction, setActiveAction] = useState(-1);
	const editorRef = useRef(null);
	const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletingRowIndex, setDeletingRowIndex] = useState();
    const handleDeleteBtnClick = (rowIndex) => {
		setConfirmDelete(true);
		setDeletingRowIndex(rowIndex);
  };
	useEffect(() => {
		if (appendix) {
			const arr = Array.isArray(appendix) ? appendix.map((desc) => ({ description: desc })) : [{ description: appendix }];
			setData(arr);
		}
	}, [appendix]);

	const resetActiveRow = () => {
		setActiveAction(-1);
	};

	const handleActions = (table, row, action) => {
		if (activeAction !== -1) return;
		if (action === RowActions.Edit) table.setEditingRow(row);
		else if (action === RowActions.Add) table.setCreatingRow(true);
		setActiveAction(action);
	};

	const saveToApi = async (payload, successMsg) => {
		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.Appendix3(vesselMstId),
				data: payload,
			});
			if (res?.isSuccess) {
				emitToasterMsg("success", successMsg);
				await refetchMonitoringData?.();
				return true;
			}
			emitToasterMsg("error", res?.errorMessage);
		} catch (error) {
			emitToasterMsg("error", error.errorMessage);
		}
		return false;
	};

	const handleDelete = async (rowIndex) => {
		const updatedData = data.filter((_, idx) => idx !== rowIndex);
		const payload = updatedData.map((item) => item.description);
		if (await saveToApi(payload, "Appendix deleted successfully")) {
			setData(updatedData);
			setActiveAction(-1);
			setConfirmDelete(false);
		}
	};

	const handleSave = async ({ row, table }) => {
		const isNew = row.id?.toString().includes("mrt-row-create");
		const editorHTML = row._valuesCache.description?.trim() || "";

		const newData = isNew
			? [...data, { description: editorHTML }]
			: data.map((item, idx) => (idx === row.index ? { ...item, description: editorHTML } : item));

		const payload = newData.map((item) => item.description);
		if (await saveToApi(payload, isNew ? "Appendix added successfully" : "Appendix updated successfully")) {
			isNew ? table.setCreatingRow(null) : table.setEditingRow(null);
			setData(newData);
			setActiveAction(-1);
		}
	};

	const columns = useMemo(
		() => [
			{
				accessorKey: "description",
				header: "Description",
				Cell: ({ cell }) => <div dangerouslySetInnerHTML={{ __html: cell.getValue() || "" }} />,
				Edit: ({ cell, row }) => (
					<RichTextCellEditor
						initialValue={cell.getValue()}
						onChange={(value) => {
							row._valuesCache.description = value;
						}}
					/>
				),
			},
		],
		[]
	);

	const table = useMaterialReactTable({
		columns,
		data: data || [],
		displayColumnDefOptions: {
			"mrt-row-actions": {
				size: 40,
				minSize: 40,
				maxSize: 40,
				muiTableHeadCellProps: {
					sx: {
						width: "20px !important",
						minWidth: "10px !important",
						
						borderRight: "1px solid lightgray",
					},
				},
				muiTableBodyCellProps: {
					sx: {
						width: "20px !important",
						minWidth: "10px !important",
						
						p: 0,
						borderRight: "1px solid rgba(224, 224, 224, 1)",
					},
				},
			},
		},

		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		enableColumnActions: false,
		enableGlobalFilter: false,
		enableColumnFilters: false,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing: true,
		enablePagination: false,
		enableDensityToggle: false,
		enableFullScreenToggle: false,
		enableColumnVisibility: false,
		enableHiding: false,
		muiTableHeadCellProps: {
			sx: {
				color: "#fff",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": { borderRight: "none" },
			},
		},
		muiTablePaperProps: { sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" } },
		muiTableContainerProps: { sx: { maxHeight: "520px" } },
		muiTableBodyCellProps: () => ({
			sx: {
				borderRight: "1px solid lightgray",
				"&:last-child": { borderRight: "none" },
				overflow: "visible !important",   
    			whiteSpace: "normal",            
    			wordBreak: "break-word",         
    			height: "auto !important",        
			},
		}),
		muiTableBodyRowProps: {
			sx: {
				"&:not(:last-of-type)": { borderBottom: "1px solid lightgray" },
				verticalAlign: "top",
				height: "auto !important",  
			},
		},
		muiTableHeadRowProps: {
			sx: { borderBottom: "2px solid lightgray" },
		},

		onCreatingRowSave: handleSave,
		onEditingRowSave: handleSave,
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		renderTopToolbarCustomActions: ({ table }) => (
			<Button startIcon={<Icon icon={ICONS.ADD} />} onClick={() => handleActions(table, null, RowActions.Add)} disabled={!!table.getState().creatingRow}>
				Add
			</Button>
		),
		renderRowActions: ({ row, table }) => (
			<Box sx={{ gap: "2px" }}>
				<Tooltip title="Edit">
					<IconButton onClick={() => handleActions(table, row, RowActions.Edit)}>
						<EditIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Delete">
					<IconButton color="error" onClick={() => handleDeleteBtnClick(row?.index, true)}>
						<DeleteIcon />
					</IconButton>
				</Tooltip>
			</Box>
		),
	});

	return (
		<>
		<div className="carbon-capture-table mt-1">
			<MaterialReactTable table={table} /> 
		</div>
			<div className="confirmation-modal-container">
						<Modal
							isOpen={confirmDelete}
							modalHeader
							padding={"70px"}
							headerHeight={"50px"}
							showFooter
							onCloseClickListener={() => {
								setConfirmDelete(false);
							}}
							footer={
								<div className="d-flex justify-content-end fixed-action-btns p-4">
									<Button
										className="modal-delete-btn"
										onClick={() => {
											handleDelete(deletingRowIndex);
										}}
										primaryColor={COLORS.RED.DARK}
										primaryBackgroundColor={COLORS.RED.LIGHT}
									>
										Delete
									</Button>
		
									<Button
										className="modal-cancel-btn"
										onClick={() => {
											setConfirmDelete(false);
										}}
									>
										Cancel
									</Button>
								</div>
							}
						>
							<div>Are you sure you want to delete this item?</div>
						</Modal>
					</div>
		</>
	);
};

export default Appendix;
